import { useEffect, useMemo, useState } from "react";
import { reverse360 } from "@/api";
import { useAuth } from "@/auth";
import {
  MessageCircle, Send, CheckCircle2, AlertCircle, ShieldCheck, SlidersHorizontal,
  CheckSquare, Type, Eye, Sparkles,
} from "lucide-react";

const TYPE_META = {
  scale:           { label: "Escala 0–10",     icon: SlidersHorizontal, color: "var(--lala-blue)" },
  yes_no:          { label: "Sí / No / NA",    icon: CheckSquare,       color: "#E4002B" },
  open:            { label: "Texto abierto",   icon: Type,              color: "#009639" },
};

const DIM_COLORS = {
  communication:        "#0033AD",
  payments:             "#E4002B",
  fairness:             "#D4A437",
  innovation_openness:  "#7C3AED",
  operational_clarity:  "#009639",
  long_term_partnership:"#0EA5E9",
};

export default function Reverse360() {
  const { user } = useAuth();
  const [questions, setQuestions] = useState([]);
  const [dimensions, setDimensions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [comment, setComment] = useState("");
  const [existing, setExisting] = useState(null);
  const [period, setPeriod] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    reverse360.dimensions().then((d) => setDimensions(d.dimensions));
    reverse360.questions().then(setQuestions);
  }, []);

  useEffect(() => {
    if (!period) return;
    reverse360.mine(period).then((r) => {
      setExisting(r);
      if (r?.answers) {
        const map = {};
        r.answers.forEach((a) => { map[a.question_id] = a; });
        setAnswers(map);
        setComment(r.comment_for_lala || "");
      } else {
        setAnswers({}); setComment("");
      }
      setMsg(""); setErr("");
    });
  }, [period]);

  const grouped = useMemo(() => {
    const g = {};
    dimensions.forEach((d) => { g[d.key] = []; });
    questions.forEach((q) => {
      if (!g[q.pillar]) g[q.pillar] = [];
      g[q.pillar].push(q);
    });
    return g;
  }, [questions, dimensions]);

  const updateAnswer = (qid, patch) => {
    setAnswers((prev) => ({ ...prev, [qid]: { ...(prev[qid] || { question_id: qid }), ...patch } }));
  };

  const isAnswered = (q, a) => {
    if (!a) return false;
    if (q.type === "scale") return a.value_scale !== undefined && a.value_scale !== null;
    if (q.type === "yes_no") return !!a.value_yes_no;
    if (q.type === "open") return !!(a.value_text && a.value_text.trim().length > 0);
    return false;
  };

  const progress = useMemo(() => {
    const total = questions.length;
    if (!total) return { total: 0, done: 0, pct: 0 };
    const done = questions.filter((q) => isAnswered(q, answers[q.id])).length;
    return { total, done, pct: Math.round((done / total) * 100) };
  }, [questions, answers]);

  const submit = async () => {
    setBusy(true); setErr(""); setMsg("");
    try {
      const payload = {
        period,
        answers: Object.values(answers).filter((a) => a && a.question_id),
        comment_for_lala: comment.trim() || null,
      };
      const res = await reverse360.submit(payload);
      setExisting(res);
      setMsg("Gracias por tu feedback. Lala lo recibe de forma anónima en el reporte agregado.");
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  if (user?.role !== "supplier") {
    return (
      <div className="max-w-3xl mx-auto px-6 py-20 text-center">
        <div className="font-display text-3xl">Esta página es para proveedores.</div>
        <p className="text-[var(--ink-soft)] mt-2">
          Si eres parte del equipo Lala y quieres ver el feedback agregado, ve a{" "}
          <a href="/admin/feedback-lala" className="text-[var(--lala-blue)] hover:underline">/admin/feedback-lala</a>.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-6 py-10" data-testid="reverse360-page">
      <div className="mb-6">
        <div className="font-hand text-xl text-[var(--lala-blue)]">cuestionario 360° invertido</div>
        <h1 className="font-display text-4xl md:text-5xl">Tu voz: evalúa a Lala</h1>
        <p className="text-[var(--ink-soft)] mt-2 max-w-3xl">
          Cada período tienes una caja segura y anónima donde nos calificas: comunicación, pagos,
          trato, apertura a innovación, claridad y visión de largo plazo. <strong>Lala usa este feedback
          agregado</strong> (nunca individual) para mejorar.
        </p>
      </div>

      <details className="sketch-border bg-[var(--cream-soft)] p-5 mb-6" open={!existing}>
        <summary className="cursor-pointer font-display text-lg text-[var(--lala-blue)] inline-flex items-center gap-2 select-none">
          <ShieldCheck size={18} /> ¿Cómo se usa este cuestionario?
        </summary>
        <div className="grid md:grid-cols-2 gap-x-8 gap-y-3 text-sm mt-4">
          <Helper title="1 · Anónimo en el reporte"
            body="Lala ve los promedios y comentarios sin tu nombre asociado. Solo se identifica la categoría (Materia prima cárnica, Empaque, Indirectos)." />
          <Helper title="2 · Sin penalización"
            body="Tu feedback no afecta negativamente tu calificación. Al contrario: Lala lo usa para ajustar procesos." />
          <Helper title="3 · Una vez por período"
            body="Puedes editar tus respuestas hasta el cierre del período. Después queda guardado." />
          <Helper title="4 · Comparte algo crítico"
            body="Si hay algo que sientes injusto o crítico, déjalo en el campo de texto abierto. Lala lee 100% de los comentarios." />
        </div>
      </details>

      <div className="grid sm:grid-cols-2 gap-4 mb-6">
        <div>
          <label className="text-xs uppercase tracking-wider text-[var(--ink-soft)] font-semibold">Período</label>
          <input value={period} onChange={(e) => setPeriod(e.target.value)}
            className="input-field mt-1 w-40" placeholder="2026-02" data-testid="reverse-period" />
        </div>
        <div className="sketch-border-soft bg-[var(--cream)] p-3">
          <div className="text-[10px] uppercase tracking-wider text-[var(--ink-soft)] font-semibold mb-1">Progreso</div>
          <div className="flex items-baseline justify-between">
            <span className="font-display text-2xl text-[var(--lala-blue)]">{progress.done}</span>
            <span className="text-[var(--ink-soft)] text-sm"> / {progress.total} respuestas</span>
          </div>
          <div className="h-1.5 mt-1 bg-[var(--cream-soft)] rounded-full overflow-hidden">
            <div className="h-full bg-[var(--lala-blue)]" style={{ width: `${progress.pct}%` }} />
          </div>
        </div>
      </div>

      {existing && (
        <div className="sketch-border-soft bg-green-50 border-green-300 p-4 mb-6 text-sm text-green-800 inline-flex items-start gap-2"
          data-testid="reverse-existing">
          <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
          <div>
            Ya enviaste tu evaluación para <b>{existing.period}</b> el {new Date(existing.submitted_at).toLocaleDateString("es-MX")}.
            Calificación general que diste a Lala: <b className="font-display text-xl">{existing.overall_score?.toFixed?.(2) ?? "—"}</b>.
            Puedes ajustar y re-enviar mientras el período esté abierto.
          </div>
        </div>
      )}

      <div className="space-y-6">
        {dimensions.map((d) => {
          const qs = grouped[d.key] || [];
          if (qs.length === 0) return null;
          const dimDone = qs.filter((q) => isAnswered(q, answers[q.id])).length;
          return (
            <section key={d.key} className="sketch-border bg-[var(--cream-soft)] p-5" data-testid={`reverse-dim-${d.key}`}
              style={{ borderColor: DIM_COLORS[d.key] }}>
              <div className="flex flex-wrap items-baseline justify-between gap-3 mb-3">
                <h2 className="font-display text-2xl" style={{ color: DIM_COLORS[d.key] }}>{d.label}</h2>
                <div className="text-xs text-[var(--ink-soft)] inline-flex items-center gap-1">
                  <CheckCircle2 size={12} /> {dimDone} / {qs.length}
                </div>
              </div>
              <div className="space-y-4">
                {qs.map((q) => (
                  <QuestionRow key={q.id} q={q} answer={answers[q.id]}
                    onChange={(patch) => updateAnswer(q.id, patch)} />
                ))}
              </div>
            </section>
          );
        })}
      </div>

      <section className="sketch-border bg-[var(--cream-soft)] p-5 mt-6" data-testid="reverse-comment-section">
        <div className="font-hand text-xl text-[var(--lala-blue)] flex items-center gap-2">
          <MessageCircle size={18} /> mensaje abierto para Lala
        </div>
        <p className="text-sm text-[var(--ink-soft)] mt-1">
          Si quieres dejar algo más allá de las preguntas, escríbelo aquí. <strong>Lala lee 100% de los comentarios.</strong>
        </p>
        <textarea rows={4} value={comment} onChange={(e) => setComment(e.target.value)}
          className="input-field mt-3" placeholder="Cualquier cosa que quieras compartir…"
          data-testid="reverse-comment" />
      </section>

      <div className="mt-8 flex flex-wrap items-center gap-3">
        <button onClick={submit} disabled={busy || progress.done === 0}
          className="btn-primary inline-flex items-center gap-2 disabled:opacity-50" data-testid="reverse-submit">
          <Send size={16} /> {busy ? "Enviando…" : existing ? "Actualizar mi evaluación" : "Enviar evaluación"}
        </button>
        {msg && <div className="text-green-700 bg-green-50 border border-green-200 rounded-lg p-2 text-sm" data-testid="reverse-success">{msg}</div>}
        {err && <div className="text-red-700 bg-red-50 border border-red-200 rounded-lg p-2 text-sm" data-testid="reverse-error">{err}</div>}
      </div>
    </div>
  );
}

function Helper({ title, body }) {
  return (
    <div className="flex items-start gap-2">
      <CheckCircle2 size={14} className="text-[var(--lala-blue)] mt-0.5 shrink-0" />
      <div>
        <div className="font-semibold text-[var(--ink)]">{title}</div>
        <div className="text-[var(--ink-soft)]">{body}</div>
      </div>
    </div>
  );
}

function QuestionRow({ q, answer, onChange }) {
  const tmeta = TYPE_META[q.type] || TYPE_META.scale;
  const Icon = tmeta.icon;
  return (
    <div className="bg-[var(--cream)] sketch-border-soft p-4" data-testid={`reverse-q-${q.id}`}>
      <div className="flex items-start gap-3">
        <div className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center" style={{ backgroundColor: tmeta.color }}>
          <Icon size={16} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-[var(--ink)]">{q.text}</div>
          {q.help_text && <div className="text-xs text-[var(--ink-soft)] italic mt-0.5">{q.help_text}</div>}
        </div>
      </div>
      <div className="mt-3 pl-12">
        {q.type === "scale" && (
          <div className="flex items-center gap-3">
            <input type="range" min="0" max="10" step="0.5"
              value={answer?.value_scale ?? 8}
              onChange={(e) => onChange({ value_scale: Number(e.target.value) })}
              className="flex-1 accent-[var(--lala-blue)]" data-testid={`q-scale-${q.id}`} />
            <span className="font-display text-2xl text-[var(--lala-blue)] w-14 text-right">
              {Number(answer?.value_scale ?? 8).toFixed(1)}
            </span>
          </div>
        )}
        {q.type === "yes_no" && (
          <div className="flex gap-2">
            {[{ v: "yes", label: "Sí" }, { v: "no", label: "No" }, { v: "na", label: "N/A" }].map((o) => (
              <button key={o.v} type="button"
                onClick={() => onChange({ value_yes_no: o.v })}
                data-testid={`q-yesno-${q.id}-${o.v}`}
                className={`sketch-pill px-4 py-1.5 text-sm font-semibold ${
                  answer?.value_yes_no === o.v
                    ? "bg-[var(--lala-blue)] text-[var(--cream)]"
                    : "bg-[var(--cream-soft)] text-[var(--lala-blue)] border border-[var(--lala-blue)]"
                }`}>{o.label}</button>
            ))}
          </div>
        )}
        {q.type === "open" && (
          <textarea rows={2} value={answer?.value_text || ""}
            onChange={(e) => onChange({ value_text: e.target.value })}
            className="input-field" placeholder="Tu respuesta…" data-testid={`q-open-${q.id}`} />
        )}
      </div>
    </div>
  );
}
